/**
 * Source upload pipeline.
 *
 * Two phases per file:
 *   1. Browser-side sniff via MediaInfo.js — reads only the container
 *      header (a few hundred KB at most for any container) and returns
 *      structural compliance findings before any byte hits the wire.
 *   2. Resumable upload via TUS — chunked, retrying, parallel-stream-
 *      capable, survives connection drops, supports cross-device resume.
 *
 * After the upload completes, the server runs the full source pre-flight
 * (head/tail black, FFEC marker detection, BS.1770 loudness, true peak,
 * etc.) and writes the .source-qc.json sidecar that the Sources page
 * reads to display the compliance chip.
 */

import * as tus from 'tus-js-client';

// MediaInfoModule.wasm is copied into frontend/public/ at build time so it
// lands at /MediaInfoModule.wasm in production. We serve it from a stable
// root path because mediainfo.js's internal `new URL('MediaInfoModule.wasm',
// import.meta.url)` can't be statically resolved by Vite, and locateFile
// alone is not enough — emscripten consults both. Pointing both at the
// same root URL is the only reliable cross-browser fix.
const MEDIAINFO_WASM_URL = '/MediaInfoModule.wasm';

let _miPromise = null;

async function loadMediaInfo() {
  if (_miPromise) return _miPromise;
  _miPromise = (async () => {
    const mod = await import('mediainfo.js');
    const MediaInfoFactory = mod.default || mod;
    return MediaInfoFactory({
      format: 'object',
      coverData: false,
      locateFile: () => MEDIAINFO_WASM_URL,
    });
  })();
  return _miPromise;
}

/**
 * Read a slice of a File via the FileReader API. Returns a Uint8Array.
 */
function readChunk(file, chunkSize, offset) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(new Uint8Array(e.target.result));
    reader.onerror = reject;
    const end = Math.min(offset + chunkSize, file.size);
    reader.readAsArrayBuffer(file.slice(offset, end));
  });
}

// v10.21.15: only HEVC hard-fails the browser sniff. HEVC long-GOP
// has real decode issues at scale (encoder fleet handles it slowly,
// CPU-bound, no NVENC fallback for some profiles). AVC / H.264 is
// fine for watermark sources — backend already passes it on watermark
// and warns only on DCP-master use. Pre-fix the browser blocked AVC
// outright with no override path, so a 21 MB HHMG-WatermarkDemo.mp4
// got rejected for being H.264 even though that's the canonical
// distribution codec.
const HARD_FAIL_CODECS = new Set(['hevc', 'h.265', 'h265']);
const SOFT_WARN_CODECS = new Set(['avc', 'h.264', 'h264', 'mpeg-4', 'mpeg4', 'xvid']);
const INTERMEDIATE_CODECS = new Set(['prores', 'apch', 'apcs', 'apco', 'apcn', 'ap4h', 'ap4x', 'dnxhd', 'dnxhr', 'avc-intra', 'jpeg 2000', 'jpeg2000', 'ffv1', 'v210', 'rawvideo', 'uncompressed']);

function _classifyCodec(codec) {
  const c = (codec || '').toLowerCase();
  for (const k of INTERMEDIATE_CODECS) if (c.includes(k)) return 'intermediate';
  if (HARD_FAIL_CODECS.has(c) || c.startsWith('hevc') || c.startsWith('h265') || c === 'h.265') return 'hard-fail';
  for (const k of SOFT_WARN_CODECS) if (c.includes(k)) return 'long-gop';
  return 'unknown';
}

/**
 * Run a browser-side MediaInfo sniff against a local File. Returns a
 * structured compliance dict. Never throws — wraps errors as warnings.
 *
 * Signature mirrors backend services/source_qc.py for consistency.
 */
export async function sniffSource(file) {
  const checks = [];
  let metadata = {};
  try {
    const mi = await loadMediaInfo();
    const result = await mi.analyzeData(
      () => file.size,
      (chunkSize, offset) => readChunk(file, chunkSize, offset)
    );
    const tracks = (result?.media?.track || []);
    const general = tracks.find(t => t['@type'] === 'General') || {};
    const video   = tracks.find(t => t['@type'] === 'Video') || null;
    const audio   = tracks.find(t => t['@type'] === 'Audio') || null;
    // v10.21.51: same "discrete mono 5.1 layout" handling as the
    // server-side QC. Pro masters often come as N mono tracks rather
    // than one multi-channel track; MediaInfo lists each as a
    // separate Audio track with Channels=1. The first track's
    // Channels count alone is misleading. If 2+ audio tracks are
    // all mono, the effective channel count is the track count.
    const allAudio = tracks.filter(t => t['@type'] === 'Audio');
    const monoTrackCount = allAudio.filter(t => parseInt(t.Channels) === 1).length;
    const isDiscreteMono = allAudio.length >= 2 && monoTrackCount === allAudio.length;
    // QuickTime sources commonly carry their start timecode in a separate
    // "Other" track of type "Time code" rather than embedded in the Video
    // track. Check both, plus the General/Video fallbacks.
    const tcTrack = tracks.find(t =>
      (t['@type'] === 'Other' || t['@type'] === 'Time code' || t['@type'] === 'Time') &&
      (t.TimeCode_FirstFrame || t.Format === 'QuickTime TC' || (t.Type && /time/i.test(t.Type)))
    ) || null;
    const startTc =
      tcTrack?.TimeCode_FirstFrame ||
      video?.TimeCode_FirstFrame ||
      general?.TimeCode_FirstFrame ||
      '';

    metadata = {
      container:        general.Format || general.Format_Profile || '',
      codec:            video?.Format || '',
      codec_profile:    video?.Format_Profile || '',
      width:            parseInt(video?.Width) || 0,
      height:           parseInt(video?.Height) || 0,
      fps:              parseFloat(video?.FrameRate) || 0,
      duration_seconds: parseFloat(general.Duration) || 0,
      start_timecode:   startTc,
      audio_channels:   isDiscreteMono ? allAudio.length : (parseInt(audio?.Channels) || 0),
      audio_sample_rate: parseInt(audio?.SamplingRate) || 0,
      audio_bit_depth:  parseInt(audio?.BitDepth) || 0,
      audio_format:     audio?.Format || '',
      file_size_bytes:  file.size,
    };

    // ── Critical structural checks ──
    if (!video) {
      checks.push({ name: 'Video stream', status: 'fail',
        message: 'No video stream detected. Confirm this is a video master and not an audio-only or graphics file.' });
    } else {
      const codecClass = _classifyCodec(metadata.codec);
      if (codecClass === 'hard-fail') {
        // HEVC / H.265 only. Real decode problem on the encoder
        // fleet — operator can't recover by toggling a flag.
        checks.push({ name: 'Codec', status: 'fail',
          message: `${metadata.codec} can't be processed. Re-encode to H.264, ProRes, or DNxHR.` });
      } else if (codecClass === 'long-gop') {
        // AVC / H.264 / MPEG-4. Fine for watermarks (transcoded
        // anyway); only DCP-as-master prefers an intermediate.
        checks.push({ name: 'Codec', status: 'warn',
          message: `${metadata.codec} works fine for watermarks. For DCP masters, prefer ProRes or DNxHR.` });
      } else if (codecClass === 'intermediate') {
        checks.push({ name: 'Codec', status: 'pass', message: `${metadata.codec} (intermediate / mastering codec).` });
      } else {
        checks.push({ name: 'Codec', status: 'warn', message: `${metadata.codec || 'unknown'} codec.` });
      }

      if (metadata.width >= 3840 || metadata.height >= 2160) {
        checks.push({ name: 'Resolution', status: 'pass', message: `${metadata.width}×${metadata.height} (4K).` });
      } else if (metadata.width >= 1920 || metadata.height >= 1080) {
        checks.push({ name: 'Resolution', status: 'pass', message: `${metadata.width}×${metadata.height} (2K).` });
      } else if (metadata.width > 0) {
        checks.push({ name: 'Resolution', status: 'warn', message: `${metadata.width}×${metadata.height} (below 2K, DCP will upscale).` });
      } else {
        checks.push({ name: 'Resolution', status: 'fail', message: 'Could not read resolution from header.' });
      }

      const fps = metadata.fps;
      if (fps > 0) {
        const standardRates = [23.976, 24, 25, 30, 48, 50, 60];
        const close = standardRates.some(r => Math.abs(fps - r) < 0.05);
        if (close) {
          checks.push({ name: 'Frame rate', status: 'pass', message: `${fps.toFixed(3)} fps.` });
        } else {
          checks.push({ name: 'Frame rate', status: 'warn', message: `${fps.toFixed(3)} fps. Will conform to the nearest cinema rate.` });
        }
      }

      if (metadata.start_timecode) {
        const tc = metadata.start_timecode;
        if (tc.startsWith('01:00:00')) {
          checks.push({ name: 'Start timecode', status: 'pass', message: `${tc} (standard master TC).` });
        } else if (tc.startsWith('00:59:30')) {
          checks.push({ name: 'Start timecode', status: 'pass', message: `${tc} (bars/tone leader).` });
        } else {
          checks.push({ name: 'Start timecode', status: 'warn', message: `${tc}. Masters typically start at 01:00:00:00.` });
        }
      } else {
        checks.push({ name: 'Start timecode', status: 'warn', message: 'No embedded timecode. DCP masters typically carry 01:00:00:00.' });
      }
    }

    if (audio) {
      if ([2, 6, 8].includes(metadata.audio_channels)) {
        checks.push({ name: 'Audio channels', status: 'pass', message: `${metadata.audio_channels} channels (${metadata.audio_format || 'unknown format'}).` });
      } else if (metadata.audio_channels > 0) {
        checks.push({ name: 'Audio channels', status: 'warn', message: `${metadata.audio_channels} channels (expected stereo or 5.1).` });
      }
      if (metadata.audio_sample_rate && metadata.audio_sample_rate !== 48000) {
        checks.push({ name: 'Audio sample rate', status: 'warn', message: `${metadata.audio_sample_rate} Hz (cinema standard is 48000 Hz). Will resample.` });
      }
    } else {
      checks.push({ name: 'Audio', status: 'warn', message: 'No audio stream. Theatrical DCPs typically need audio.' });
    }
  } catch (e) {
    checks.push({ name: 'Header sniff', status: 'warn',
      message: `Browser sniff unavailable (${e?.message || e}). Full pre-flight runs after upload.` });
  }

  const statuses = checks.map(c => c.status);
  let compliance = 'pass';
  if (statuses.includes('fail')) compliance = 'fail';
  else if (statuses.includes('warn')) compliance = 'warn';

  return { compliance, checks, metadata };
}

/**
 * Classify a tus error so the upload manager can react differently to
 * "the local file went away" (re-selection needed) vs "tusd rejected
 * the upload" (terminal) vs generic transient network errors (let
 * tus-js-client retry).
 *
 * `source_unavailable` is the case we care most about — typical when an
 * SMB / network share that backs the picked file disconnects mid-upload.
 * Chrome surfaces it as `ERR_FILE_NOT_FOUND` or a `NotReadableError`,
 * Safari/Firefox have similar but different wording. We match generously
 * so we don't miss it on one engine.
 */
export function classifyTusError(err) {
  const raw = String(err?.message || err || '');
  const lower = raw.toLowerCase();
  // Browser can't read from the File anymore — unmounted SMB share,
  // deleted/moved file, ejected USB, browser tab restored without the
  // File handle, etc.
  if (
    lower.includes('notreadableerror') ||
    lower.includes('err_file_not_found') ||
    lower.includes('err_failed') ||
    lower.includes('the requested file could not be read') ||
    lower.includes('a requested file or directory could not be found') ||
    lower.includes('failed to slice') ||
    lower.includes('network error occurred while reading')
  ) {
    return 'source_unavailable';
  }
  return 'error';
}

/**
 * Start a resumable TUS upload. Returns the tus.Upload instance so the
 * caller can pause / resume / cancel.
 *
 * When the upload fails because the local File is no longer readable
 * (SMB drop, USB eject, file moved), `onSourceUnavailable` fires
 * instead of `onError` — the caller should pause the retry loop and
 * surface a "re-select file to resume" UX. The provided `uploadUrl`
 * is the tus URL on the server, which still has the partially-
 * uploaded bytes; passing it back into a new tus.Upload as
 * `uploadUrl: <saved>` resumes from the server's offset.
 */
export function startTusUpload(file, {
  endpoint,
  companyId,
  flags,
  onProgress,
  onSuccess,
  onError,
  onSourceUnavailable,
  uploadUrl,
}) {
  const upload = new tus.Upload(file, {
    endpoint,
    // Resume an existing partial upload. When set, tus-js-client skips
    // the creation POST and goes straight to PATCH against this URL.
    // Used by reselectAndResume() in UploadManagerContext.
    uploadUrl: uploadUrl || undefined,
    chunkSize: 64 * 1024 * 1024,            // 64 MiB chunks
    // v10.21.37: "super resilient" retry budget. v10.21.36 went
    // from 7 attempts / 99 s to 14 / 45 min; this raises it again
    // to 30 attempts / ~3.5 hours.
    //
    // Realistic outages the budget needs to absorb:
    //   - wifi handoff or AP reboot (5–60 s)
    //   - ISP modem reboot (5–15 min)
    //   - laptop sleep + wake with stale network (up to ~1 h)
    //   - extended ISP outage (could be hours, rare)
    //   - tusd restart from a deploy (~10 s window)
    //
    // The first 9 entries ramp from 0 to 10 min so the common
    // brief blip recovers in seconds, not minutes. The remaining
    // 21 entries sit at the 10-min cap so a multi-hour outage
    // doesn't silently abort. Total budget = ~3.5 hours.
    //
    // Fatal errors (4xx from hooks, source_unavailable) bypass
    // this budget entirely via `onShouldRetry` below — so a real
    // rejection still surfaces immediately and doesn't waste the
    // 3-hour window retrying something that's never going to work.
    retryDelays: [
      0, 1000, 3000, 5000, 10000, 30000, 60000, 120000, 300000,
      600000, 600000, 600000, 600000, 600000, 600000, 600000, 600000,
      600000, 600000, 600000, 600000, 600000, 600000, 600000, 600000,
      600000, 600000, 600000, 600000, 600000,
    ],
    // v10.21.37: skip retries on terminal errors. tus-js-client
    // retries every error by default, which on a 4xx (file
    // rejected by the post-receive hook, auth-fail) burns the
    // 3-hour budget on something that's never going to succeed.
    // Returning false from this callback aborts immediately and
    // fires onError so the user sees the real message.
    onShouldRetry: (err, retryAttempt, options) => {
      // 4xx is the server saying "no" — except 408 Request Timeout
      // and 429 Too Many Requests which are transient.
      const status = err?.originalResponse?.getStatus?.();
      if (typeof status === 'number' && status >= 400 && status < 500
          && status !== 408 && status !== 429) {
        return false;
      }
      // Local File handle gone → no point retrying, user needs to
      // re-pick the file (handled by onError → onSourceUnavailable).
      if (classifyTusError(err) === 'source_unavailable') {
        return false;
      }
      // Everything else — 5xx, network errors, generic — retry.
      return true;
    },
    parallelUploads: 1,
    metadata: {
      filename: file.name,
      filetype: file.type || 'application/octet-stream',
      companyId: String(companyId),
      flags: JSON.stringify(flags || { watermarks: true, dcp: true }),
    },
    onError: (err) => {
      // tusd post-receive may reject the file; surface the structured message
      let msg = err?.message || String(err);
      try {
        const body = err?.originalResponse?.getBody?.() || '';
        if (body) {
          const parsed = JSON.parse(body);
          if (parsed.message) msg = parsed.message;
        }
      } catch (_) { /* ignore */ }

      // Local-file-gone case: stop the retry loop and tell the manager
      // we need a new File handle. Without this, tus-js-client retries
      // forever (each PATCH faceplants on the next slice read) until
      // the user manually cancels — which loses the partial bytes on
      // the server unnecessarily.
      if (classifyTusError(err) === 'source_unavailable' && onSourceUnavailable) {
        try { upload.abort(false); } catch (_) { /* ignore */ }
        onSourceUnavailable({
          url: upload.url,                  // tus PATCH target on the server
          message: 'Lost access to the source file. The drive or share that was holding it may have disconnected, or the file moved. Re-select the file to resume from where the upload left off.',
          rawMessage: msg,
        });
        return;
      }

      onError && onError(msg);
    },
    onProgress: (sent, total) => onProgress && onProgress(sent, total),
    onSuccess: () => onSuccess && onSuccess(),
  });
  upload.start();
  return upload;
}
